import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ArrowLeft, ImageIcon, X, Check, Film, Lock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import MindfulChatBot from "@/components/MindfulChatBot";
import ParticleBackground from "@/components/ParticleBackground";
import ConversationHistoryPanel from "@/components/mindful/ConversationHistoryPanel";
import MindfulVaultDialog from "@/components/mindful/MindfulVaultDialog";
import { useVisionBoard } from "@/hooks/useVisionBoard";
import { useMindfulVault } from "@/hooks/useMindfulVault";
import { useMindfulChat } from "@/hooks/useMindfulChat";
import { cn } from "@/lib/utils";

const BG_KEY = "mindful-chat-background";

const DashboardMindfulChat: React.FC = () => {
  const { images } = useVisionBoard();
  const [bgUrl, setBgUrl] = useState<string | null>(() => localStorage.getItem(BG_KEY));
  const [pickerOpen, setPickerOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [pickerPos, setPickerPos] = useState({ top: 0, right: 0 });

  const vault = useMindfulVault();
  const [vaultDialogOpen, setVaultDialogOpen] = useState(false);

  const persistence = useMemo(
    () => ({
      enabled: vault.status === "unlocked",
      saveConversation: vault.saveConversation,
      loadConversation: vault.loadConversation,
    }),
    [vault.status, vault.saveConversation, vault.loadConversation]
  );

  const { messages, isTyping, sendMessage, conversationId, startNewConversation, openConversation } =
    useMindfulChat(persistence);

  useEffect(() => {
    if (vault.status === "needs-setup" || vault.status === "locked") setVaultDialogOpen(true);
  }, [vault.status]);

  useEffect(() => {
    if (bgUrl) localStorage.setItem(BG_KEY, bgUrl);
    else localStorage.removeItem(BG_KEY);
  }, [bgUrl]);

  const openPicker = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPickerPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setPickerOpen((o) => !o);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        pickerRef.current && !pickerRef.current.contains(target) &&
        triggerRef.current && !triggerRef.current.contains(target)
      ) {
        setPickerOpen(false);
      }
    };
    if (pickerOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  const selectBg = (url: string) => {
    setBgUrl(url === bgUrl ? null : url);
    setPickerOpen(false);
  };

  const handleDelete = async (id: string) => {
    await vault.deleteConversation(id);
    if (conversationId === id) startNewConversation();
  };

  return (
    <div className="relative flex flex-col h-[calc(100vh-64px)] w-full overflow-hidden">
      {/* Background layer */}
      {bgUrl ? (
        bgUrl.match(/\.(mp4|webm|mov)(\?|$)/i) ? (
          <video
            key={bgUrl}
            src={bgUrl}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 z-0 w-full h-full object-cover"
          />
        ) : (
          <div
            className="absolute inset-0 z-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${bgUrl})` }}
          />
        )
      ) : (
        <ParticleBackground />
      )}

      {/* Overlay for readability */}
      {bgUrl && <div className="absolute inset-0 z-0 bg-background/20" />}

      {/* Header */}
      <div className="relative z-10 px-4 pt-4 pb-2 bg-background/60 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <Link
            to="/dashboard"
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors text-sm"
          >
            <ArrowLeft size={16} />
            Back
          </Link>
          <span className="text-sm font-medium text-foreground flex-1">Mindful Chat</span>

          {vault.status !== "unlocked" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setVaultDialogOpen(true)}
              className="shrink-0 text-muted-foreground hover:text-foreground gap-1.5 h-8"
            >
              {vault.status === "locked" ? <Lock className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
              <span className="text-xs">{vault.status === "locked" ? "Unlock history" : "Private history"}</span>
            </Button>
          )}

          {bgUrl && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setBgUrl(null)}
              className="shrink-0 text-muted-foreground hover:text-destructive h-8 w-8"
              title="Remove background"
            >
              <X className="h-4 w-4" />
            </Button>
          )}

          <Button
            ref={triggerRef}
            variant="ghost"
            size="icon"
            onClick={openPicker}
            className={cn("shrink-0 relative h-8 w-8", bgUrl && "text-primary")}
            title="Set background from Vision Board"
          >
            <ImageIcon className="h-4 w-4" />
            {bgUrl && (
              <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary" />
            )}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground/80 mt-1">
          Private and end-to-end encrypted. Only you can read these conversations.
        </p>
      </div>

      {/* Chat */}
      <div className="relative z-10 flex-1 flex min-h-0">
        {vault.status === "unlocked" && (
          <ConversationHistoryPanel
            conversations={vault.conversations}
            activeId={conversationId}
            onSelect={openConversation}
            onDelete={handleDelete}
            onNew={startNewConversation}
            onLock={vault.lockVault}
          />
        )}
        <div className="flex-1 flex flex-col min-h-0">
          <MindfulChatBot messages={messages} isTyping={isTyping} onSendMessage={sendMessage} />
        </div>
      </div>

      <MindfulVaultDialog
        open={vaultDialogOpen && (vault.status === "needs-setup" || vault.status === "locked")}
        status={vault.status}
        onSetup={vault.setupVault}
        onUnlock={vault.unlockVault}
        onReset={vault.resetVault}
        onSkip={() => { vault.skipVault(); setVaultDialogOpen(false); }}
        onClose={() => setVaultDialogOpen(false)}
      />

      {/* Background picker portal */}
      {pickerOpen && createPortal(
        <div
          ref={pickerRef}
          style={{ position: "fixed", top: pickerPos.top, right: pickerPos.right, zIndex: 99999 }}
          className="w-72 bg-popover border border-border rounded-xl shadow-2xl p-3"
        >
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Pick a background from your Vision Board
          </p>
          {images.length === 0 ? (
            <p className="text-xs text-muted-foreground italic text-center py-4">
              No images on your Vision Board yet. Add some first!
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
              {images.map((img) => (
                <button
                  key={img.id}
                  onClick={() => selectBg(img.url)}
                  className={cn(
                    "relative aspect-video rounded-lg overflow-hidden border-2 transition-all hover:opacity-90 focus:outline-none",
                    bgUrl === img.url ? "border-primary" : "border-transparent"
                  )}
                >
                  {img.type === "video" ? (
                    <>
                      <video
                        src={img.url}
                        muted
                        loop
                        autoPlay
                        playsInline
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-1 right-1 bg-black/60 rounded px-1">
                        <Film className="h-2.5 w-2.5 text-white" />
                      </div>
                    </>
                  ) : (
                    <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
                  )}
                  {bgUrl === img.url && (
                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                      <Check className="h-4 w-4 text-primary-foreground drop-shadow" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default DashboardMindfulChat;
